import { ChangeDetectionStrategy, Component, forwardRef, input, output, signal } from '@angular/core';
import { ControlValueAccessor, NG_VALUE_ACCESSOR } from '@angular/forms';
import { CustomerSurvey, CustomerSurveyAnswer, CustomerSurveyQuestion } from '@coin/shared/customer-survey/util';
import { Fn } from '@coin/shared/core/util-models';

@Component({
  selector: 'coin-customer-survey',
  templateUrl: './customer-survey.component.html',
  styleUrls: ['./customer-survey.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => CustomerSurveyComponent),
      multi: true
    }
  ],
  standalone: false
})
export class CustomerSurveyComponent implements ControlValueAccessor {
  isLocked = input(false);
  completeSurvey = output<void>();

  public expandedPanelIndex = signal(-1);

  private _value = signal<CustomerSurvey>(undefined);
  set value(value: CustomerSurvey) {
    if (value == null) {
      return;
    }

    this._value.set(value);
    this.onChange(value);
    this.onTouch();
  }
  get value() {
    return this._value();
  }

  writeValue(value: CustomerSurvey): void {
    this._value.set(value);
  }

  onChange: Fn = () => {};
  registerOnChange(fn: Fn): void {
    this.onChange = fn;
  }

  onTouch: Fn = () => {};
  registerOnTouched(fn: Fn): void {
    this.onTouch = fn;
  }

  isDisabled = signal(false);
  setDisabledState(isDisabled: boolean) {
    this.isDisabled.set(isDisabled);
  }

  /** Reset answers and start with first question */
  public resetSurvey(): void {
    const updatedSurvey = {
      ...this.value,
      questions: this.value.questions.map((question, index) => ({
        ...question,
        isActive: index === 0,
        selectedAnswerId: undefined,
        answers: question.answers.map(answer => ({ ...answer, isSelected: false }))
      }))
    };
    this.writeValue(updatedSurvey);
    this.expandedPanelIndex.set(0);
  }

  public onAnswerToggle(answer: CustomerSurveyAnswer): void {
    if (!answer) return;

    const updatedSurvey = { ...this.value };
    updatedSurvey.questions = resetOpenQuestions(updatedSurvey, updatedSurvey.questions, answer);

    if (answer.linkedSurveyQuestionId) {
      updatedSurvey.questions = setNextQuestionToActive(updatedSurvey.questions, answer);
    }

    this.value = updatedSurvey;
  }
}

function resetOpenQuestions(
  survey: CustomerSurvey,
  questions: CustomerSurveyQuestion[],
  answer: CustomerSurveyAnswer
): CustomerSurveyQuestion[] {
  const currentLevel = survey.questions.find(question =>
    question.answers.some(answerQuestion => answerQuestion.id === answer.id)
  )?.treeLevel;
  return questions.map(question => {
    return { ...question, isActive: question.treeLevel > currentLevel ? false : question.isActive };
  });
}

function setNextQuestionToActive(
  questions: CustomerSurveyQuestion[],
  answer: CustomerSurveyAnswer
): CustomerSurveyQuestion[] {
  return questions.map(question => {
    const isNextQuestion = question.id === answer.linkedSurveyQuestionId;
    if (!isNextQuestion) return question;

    return {
      ...question,
      isActive: true,
      selectedAnswerId: undefined,
      answers: question.answers.map(a => ({ ...a, isSelected: false }))
    };
  });
}
